import { useState } from "react";
import cx from "classnames";
import { createPortal } from "react-dom";
import { useLocalStorage } from "usehooks-ts";
import { authAxios as axios } from "shared-auth";
import { config } from "component-library/src/config";

import {
  AgenciesWithFilmmakersPopUp,
  AgenciesWithFilmmakers,
} from "component-library";
import { FMLogoDark } from "assets";
import { IS_FILMMAKERS_LOG_IN_MODAL_HIDDEN } from "../../Organisms/AgenciesWithFilmmakersPopUp/AgenciesWithFilmmakersPopUp";
import { AgenciesWithFilmmakersErrorPopUp } from "../../Organisms/AgenciesWithFilmmakersPopUp/AgenciesWithFilmmakersErrorPopUp";

interface Props {
  agenciesWithFilmmakers: AgenciesWithFilmmakers[];
  title: string;
  isTextBold?: boolean;
}

const ViewJobsOnFilmmakersButtonWrapper = ({
  agenciesWithFilmmakers,
  title,
  isTextBold,
}: Props) => {
  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [isFilmmakersLogInModalHidden, setIsFilmmakersLogInModalHidden] =
    useLocalStorage<boolean>(IS_FILMMAKERS_LOG_IN_MODAL_HIDDEN, false);

  const filteredAgenciesWithFilmmakers = agenciesWithFilmmakers?.filter(
    (agency) => agency.FilmmakersAgencyId
  );

  const isOnlyOneAgencyLinkedToSpotlightAccount =
    agenciesWithFilmmakers?.length > 1 &&
    agenciesWithFilmmakers?.filter((item) => item.FilmmakersAgencyId).length ===
      1;

  const shouldSubmitWithoutPopup =
    (isFilmmakersLogInModalHidden && isOnlyOneAgencyLinkedToSpotlightAccount) ||
    (agenciesWithFilmmakers.length === 1 &&
      Boolean(agenciesWithFilmmakers[0].FilmmakersAgencyId));

  const handleClose = () => {
    setIsPopupVisible(false);

    if (isError) {
      setIsError(false);
    }
  };

  const handleLogIntoFilmmakersSSO = async (
    agencyRefNumber: number,
    isHidden?: boolean
  ) => {
    try {
      setIsError(false);

      const { data: url } = await axios(
        `${config.liteAgentApiUrl}/agencies/${agencyRefNumber}/filmmakers-sso-link`,
        { params: { skipGenericErrorHandling: true } }
      );

      window.open(typeof url === "string" ? url : String(url), "_blank");

      if (isHidden) {
        setIsFilmmakersLogInModalHidden(isHidden);
      }

      handleClose();
    } catch (error) {
      console.error(error);

      setIsError(true);
    }
  };

  return (
    <>
      <button
        className={cx("flex gap-1.5", {
          "font-bold": isTextBold,
        })}
        disabled={isError}
        onClick={async () => {
          if (shouldSubmitWithoutPopup) {
            await handleLogIntoFilmmakersSSO(
              filteredAgenciesWithFilmmakers[0].AgencyRefNumber
            );
          } else {
            setIsPopupVisible(true);
          }
        }}
      >
        {title} <FMLogoDark height={23} width={100} />
      </button>

      {isError &&
        shouldSubmitWithoutPopup &&
        createPortal(
          <AgenciesWithFilmmakersErrorPopUp handleClose={handleClose} />,
          document.body
        )}

      {isPopupVisible &&
        !shouldSubmitWithoutPopup &&
        createPortal(
          <AgenciesWithFilmmakersPopUp
            isOnlyOneAgencyLinkedToSpotlightAccount={
              isOnlyOneAgencyLinkedToSpotlightAccount
            }
            filteredAgenciesWithFilmmakers={filteredAgenciesWithFilmmakers}
            onHandleClose={handleClose}
            handleLogIntoFilmmakersSSO={handleLogIntoFilmmakersSSO}
            isError={isError}
            handleCloseError={() => setIsError(false)}
          />,
          document.body
        )}
    </>
  );
};

export default ViewJobsOnFilmmakersButtonWrapper;
